import { FC } from 'react';
import { Box, LinearProgress, ListItem, Typography } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { formatDistanceToNow } from 'date-fns';

import type { Operation, OperationType } from './types';

const OPERATION_CONFIG: Record<OperationType, { icon: typeof FileUploadIcon; label: string; color: string }> = {
  'sample-upload-import': { icon: FileUploadIcon, label: 'Import', color: '#0D9488' },
  'sample-upload-update': { icon: EditIcon, label: 'Update', color: '#F59E0B' },
  'sample-update': { icon: EditIcon, label: 'Update', color: '#F59E0B' },
  'sample-delete': { icon: DeleteIcon, label: 'Delete', color: '#EF4444' },
  'sample-reset': { icon: RefreshIcon, label: 'Reset', color: '#8B5CF6' },
  'sample-unblacklist': { icon: HowToRegIcon, label: 'Unblacklist', color: '#868787' },
  'sample-response-code-change': { icon: SyncAltIcon, label: 'RC Change', color: '#2563EB' },
};

interface OperationItemProps {
  operation: Operation;
  divider?: boolean;
}

export const OperationItem: FC<OperationItemProps> = ({ operation, divider }) => {
  const config = OPERATION_CONFIG[operation.operationType];
  const Icon = config.icon;
  const progress = (operation.processedCount / operation.totalCount) * 100;
  const timeAgo = formatDistanceToNow(new Date(operation.startedAt), { addSuffix: true });

  return (
    <ListItem
      button={false}
      divider={divider}
      sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1.25, px: 1.75 }}
    >
      <Box sx={{ mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Icon fontSize="small" sx={{ color: config.color, flexShrink: 0 }} />
            <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
              {config.label}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {timeAgo}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.25,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {operation.label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 4,
            borderRadius: 1,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': { backgroundColor: config.color },
          }}
        />
        <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ minWidth: 56, textAlign: 'right' }}>
          {operation.processedCount.toLocaleString()}/{operation.totalCount.toLocaleString()}
        </Typography>
      </Box>
    </ListItem>
  );
};
